import {html, nothing, svg, type HTMLTemplateResult} from "lit";
import {styleMap} from "lit/directives/style-map.js";

import {poolColorStyle} from "../pool-color";
import {polarPoint, sectorPath} from "../../svg-geometry";
import {fitLabel, indexIn, mod, SHORTCUT_FIT, type PickerSkinInput} from "./picker-skin-input";

const CX = 100;
const CY = 108;
const R = 88;
/** Full turns before the wheel comes to rest. */
const SPINS = 4;
/** From this pool size on, the segments are too narrow for a shortcut. */
export const WHEEL_MAX_LABELS = 24;

/** The rotation (in turns, clockwise) at which segment `index` sits centred under the pointer at the top. A
 *  single employee is the whole wheel, its shortcut drawn at the top — so its rest is 0 (review 0072-03). */
export function restRotation(n: number, index: number): number {
    return n === 1 ? 0 : mod(-(index + 0.5) / n, 1);
}

/**
 * The wheel's rotation in turns: at `progress = 0` where the draw before left it (`from`; 0 before the first
 * draw), at `progress = 1` with segment `to` under the pointer, `SPINS` full turns later.
 */
export function wheelRotation(n: number, from: number | null, to: number, progress: number): number {
    const start = from === null ? 0 : restRotation(n, from);
    const end = start + SPINS + mod(restRotation(n, to) - start, 1);
    return start + (end - start) * progress;
}

/** Glücksrad: a segment per employee with their shortcut, the pointer at the top. */
export function renderWheelSkin({pool, current, previous, progress, phase}: PickerSkinInput): HTMLTemplateResult {
    const n = pool.length;
    const to = indexIn(pool, current);
    const from = indexIn(pool, previous);
    const turns = to === null ? 0 : wheelRotation(n, phase === 'shown' ? to : from, to, phase === 'shown' ? 1 : progress);
    const labels = n <= WHEEL_MAX_LABELS;
    const degrees = turns * 360;

    return html`
        <svg class="picker-wheel" viewBox="0 0 200 200" aria-hidden="true">
            ${svg`
                <g class="picker-wheel__wheel" transform=${`rotate(${degrees.toFixed(2)} ${CX} ${CY})`}>
                    ${pool.map((entry, i) => svg`
                        <g class="picker-wheel__segment" style=${styleMap(poolColorStyle(entry.uid, entry.color, entry.textColor ?? null))}>
                            ${n === 1
                                ? svg`<circle class="picker-wheel__face" cx=${CX} cy=${CY} r=${R}></circle>`
                                : svg`<path class="picker-wheel__face" d=${sectorPath(CX, CY, R, i / n, (i + 1) / n)}></path>`}
                            ${labels ? label(fitLabel(entry.shortcut, SHORTCUT_FIT), n === 1 ? {x: CX, y: CY - R * 0.55} : polarPoint(CX, CY, R * 0.72, (i + 0.5) / n), degrees) : nothing}
                        </g>
                    `)}
                </g>
                <circle class="picker-wheel__hub" cx=${CX} cy=${CY} r="9"></circle>
                <polygon class="picker-wheel__pointer" points=${`${CX - 9},2 ${CX + 9},2 ${CX},${CY - R + 10}`}></polygon>
            `}
        </svg>
    `;
}

/** A shortcut, turned back by the wheel's rotation around its own point — it stays upright while the wheel
 *  spins, also under the pointer. */
function label(text: string, {x, y}: {x: number; y: number}, wheelDegrees: number) {
    return svg`<text class="picker-wheel__label" x=${x.toFixed(2)} y=${y.toFixed(2)}
                     transform=${`rotate(${(-wheelDegrees).toFixed(2)} ${x.toFixed(2)} ${y.toFixed(2)})`}>${text}</text>`;
}
